
import { MainLayout } from "@/components/layout/MainLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CalendarIcon, Users, Flame } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";

interface Webinar {
  id: number;
  title: string;
  date: string;
  time: string;
  participants: number;
  maxParticipants: number;
  credits: number;
  isPremiere: boolean;
}

const CoursesWebinars = () => {
  const [webinars, setWebinars] = useState<Webinar[]>([]);
  const { userCredits, setUserCredits } = useAuth();

  // Load webinars from adminFiles
  useEffect(() => {
    const adminFiles = localStorage.getItem('adminFiles');
    if (adminFiles) {
      const parsedFiles = JSON.parse(adminFiles);
      const webinarFiles = parsedFiles
        .filter((file: any) => file.category === 'webinars')
        .map((file: any) => ({
          id: file.id,
          title: file.title || file.name,
          date: file.date || file.uploadDate || "N/A",
          time: file.time || "18:00",
          participants: file.participants || 0,
          maxParticipants: file.maxParticipants || 100,
          credits: file.credits || 10,
          isPremiere: file.isPremiere || false
        }));
      
      setWebinars(webinarFiles);
    }
  }, []);

  const handleRegister = (webinarId: number, webinarCredits: number) => {
    if (userCredits < webinarCredits) {
      toast({
        title: "Ανεπαρκή Credits",
        description: "Δεν έχετε αρκετά credits για να εγγραφείτε σε αυτό το webinar.",
        variant: "destructive"
      });
      return;
    }

    // Deduct credits
    setUserCredits(prevCredits => prevCredits - webinarCredits);

    toast({
      title: "Εγγραφή σε Webinar",
      description: `Εγγραφήκατε επιτυχώς στο webinar «${webinars.find(w => w.id === webinarId)?.title}». Αφαιρέθηκαν ${webinarCredits} credits.`
    });
  };

  return (
    <MainLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Webinars</h1>
          <p className="text-muted-foreground mt-2">
            Διαδικτυακά σεμινάρια με επαγγελματίες του χώρου
          </p>
        </div>

        <div className="flex justify-between items-center bg-muted/50 p-4 rounded-lg mb-4">
          <span className="font-medium">Διαθέσιμα Credits:</span>
          <span className="font-bold text-primary">{userCredits} Credits</span>
        </div>

        {webinars.length === 0 ? (
          <div className="text-center py-16 border rounded-lg">
            <p className="text-muted-foreground">Δεν υπάρχουν διαθέσιμα webinars.</p>
            <p className="text-muted-foreground mt-2">Μπορείτε να προσθέσετε webinars από το περιβάλλον διαχείρισης.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {webinars.map((webinar) => (
              <Card key={webinar.id} className="relative overflow-hidden">
                {webinar.isPremiere && (
                  <Badge variant="destructive" className="absolute top-2 right-2 flex items-center">
                    <Flame className="h-3 w-3 mr-1" /> ΠΡΕΜΙΕΡΑ
                  </Badge>
                )}
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">{webinar.title}</CardTitle>
                  <CardDescription className="flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4" />
                    <span>{webinar.date}, {webinar.time}</span>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="aspect-video bg-muted rounded-md mb-4 flex flex-col items-center justify-center p-4">
                    <Users className="h-8 w-8 mb-2 text-muted-foreground" />
                    <span className="text-sm text-center text-muted-foreground">
                      Συμμετέχοντες: {webinar.participants} / {webinar.maxParticipants}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-primary flex items-center">
                      <span className="inline-block px-2 py-1 bg-primary/10 rounded-md text-primary font-medium mr-1">
                        {webinar.credits}
                      </span> 
                      Credits
                    </span>
                    <Button 
                      size="sm"
                      onClick={() => handleRegister(webinar.id, webinar.credits)}
                      disabled={userCredits < webinar.credits}
                    >
                      {userCredits >= webinar.credits ? 'Εγγραφή' : 'Ανεπαρκή Credits'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default CoursesWebinars;
